import type { SqlDriver } from './driver';

/**
 * Append-only list; index + 1 is the schema version stored in PRAGMA user_version.
 * Never edit a shipped migration — add a new one.
 */
export const migrations: string[] = [
  /* 1 */ `
  CREATE TABLE kv (
    key   TEXT PRIMARY KEY,
    value TEXT NOT NULL
  );

  CREATE TABLE pages (
    id         TEXT PRIMARY KEY,
    parent_id  TEXT REFERENCES pages(id),
    title      TEXT NOT NULL DEFAULT '',
    icon       TEXT,
    sort_key   TEXT NOT NULL,
    created_at INTEGER NOT NULL,
    updated_at INTEGER NOT NULL,
    trashed_at INTEGER,
    hlc        TEXT NOT NULL,
    deleted    INTEGER NOT NULL DEFAULT 0
  );
  CREATE INDEX pages_parent ON pages(parent_id, sort_key);

  -- Yjs document updates per page (editor content, Phase 1).
  CREATE TABLE page_updates (
    seq     INTEGER PRIMARY KEY AUTOINCREMENT,
    page_id TEXT NOT NULL REFERENCES pages(id),
    data    BLOB NOT NULL,
    hlc     TEXT NOT NULL,
    synced  INTEGER NOT NULL DEFAULT 0
  );
  CREATE INDEX page_updates_page ON page_updates(page_id, seq);

  -- Local changes waiting to be pushed to the cloud (Phase 4).
  CREATE TABLE outbox (
    seq       INTEGER PRIMARY KEY AUTOINCREMENT,
    entity    TEXT NOT NULL,
    entity_id TEXT NOT NULL,
    patch     TEXT NOT NULL,
    hlc       TEXT NOT NULL
  );
  `,
  /* 2 */ `
  -- Page properties (Notion-style), one row per key so each syncs independently.
  CREATE TABLE page_props (
    page_id TEXT NOT NULL REFERENCES pages(id),
    key     TEXT NOT NULL,
    value   TEXT NOT NULL,          -- JSON
    hlc     TEXT NOT NULL,
    deleted INTEGER NOT NULL DEFAULT 0,
    PRIMARY KEY (page_id, key)
  );

  -- Derived from page content; rebuilt locally, never synced.
  CREATE TABLE links (
    src_id     TEXT NOT NULL,
    dst_id     TEXT,                -- null = unresolved target
    dst_title  TEXT NOT NULL,
    kind       TEXT NOT NULL,       -- 'link' | 'embed'
    context    TEXT NOT NULL        -- text of the block containing the link
  );
  CREATE INDEX links_src ON links(src_id);
  CREATE INDEX links_dst ON links(dst_id);
  CREATE INDEX links_dst_title ON links(dst_title COLLATE NOCASE);

  CREATE TABLE tags (
    page_id TEXT NOT NULL,
    tag     TEXT NOT NULL COLLATE NOCASE
  );
  CREATE INDEX tags_tag ON tags(tag);
  CREATE INDEX tags_page ON tags(page_id);

  CREATE VIRTUAL TABLE page_fts USING fts5(
    page_id UNINDEXED, title, body,
    tokenize = 'unicode61 remove_diacritics 2'
  );

  -- Point-in-time Yjs snapshots for "previous versions".
  CREATE TABLE page_versions (
    id         TEXT PRIMARY KEY,
    page_id    TEXT NOT NULL REFERENCES pages(id),
    created_at INTEGER NOT NULL,
    state      BLOB NOT NULL
  );
  CREATE INDEX page_versions_page ON page_versions(page_id, created_at);
  `,
  /* 3 */ `
  -- Files referenced from pages (images, PDFs, …). Moves to cloud storage in Phase 4.
  CREATE TABLE attachments (
    id         TEXT PRIMARY KEY,
    name       TEXT NOT NULL,          -- file name as linked: "Diagram.png"
    path       TEXT,                   -- original path in an imported vault
    mime       TEXT NOT NULL,
    size       INTEGER NOT NULL,
    data       BLOB NOT NULL,
    created_at INTEGER NOT NULL,
    hlc        TEXT NOT NULL,
    deleted    INTEGER NOT NULL DEFAULT 0
  );
  CREATE INDEX attachments_name ON attachments(name COLLATE NOCASE);
  `,
  /* 4 */ `
  -- Checkbox tasks inside pages. Derived from page content (the document is the
  -- source of truth), rebuilt locally, never synced.
  CREATE TABLE inline_tasks (
    id          TEXT PRIMARY KEY,   -- taskId attribute of the taskItem node
    page_id     TEXT NOT NULL,
    parent_id   TEXT,               -- enclosing task for subtasks
    position    INTEGER NOT NULL,   -- order within the page
    text        TEXT NOT NULL,      -- full task text including tokens
    status_char TEXT NOT NULL,
    link_ids    TEXT NOT NULL       -- JSON array of linked page ids
  );
  CREATE INDEX inline_tasks_page ON inline_tasks(page_id, position);
  `,
  /* 5 */ `
  -- What happened to each reminder occurrence (keyed task|kind|time), so a
  -- reminder is never delivered twice and snoozes survive restarts. Per device.
  CREATE TABLE reminder_state (
    key           TEXT PRIMARY KEY,
    task_id       TEXT NOT NULL,
    status        TEXT NOT NULL,      -- sent | missed | snoozed | dismissed
    snoozed_until INTEGER,
    updated_at    INTEGER NOT NULL
  );

  -- The notification centre (bell). Per device.
  CREATE TABLE notifications (
    id         TEXT PRIMARY KEY,
    key        TEXT NOT NULL,
    kind       TEXT NOT NULL,         -- reminder | missed | summary
    task_id    TEXT,
    page_id    TEXT,
    title      TEXT NOT NULL,
    body       TEXT NOT NULL DEFAULT '',
    fire_at    INTEGER NOT NULL,
    created_at INTEGER NOT NULL,
    read_at    INTEGER
  );
  CREATE INDEX notifications_created ON notifications(created_at);
  `,
  /* 6 */ `
  -- Sync (Fase 4): a clock per page field, so offline edits merge field by field.
  ALTER TABLE pages ADD COLUMN field_hlc TEXT NOT NULL DEFAULT '{}';
  -- Content updates received from the cloud (de-duplication by server id).
  ALTER TABLE page_updates ADD COLUMN remote_id INTEGER;
  CREATE UNIQUE INDEX page_updates_remote ON page_updates(remote_id) WHERE remote_id IS NOT NULL;
  -- Attachment files uploaded to cloud storage.
  ALTER TABLE attachments ADD COLUMN uploaded INTEGER NOT NULL DEFAULT 0;
  -- Clocks of synced workspace settings (tasks folder, templates, …).
  CREATE TABLE synced_settings (
    key   TEXT PRIMARY KEY,
    value TEXT,
    hlc   TEXT NOT NULL
  );
  `,
  /* 7 */ `
  -- What the AI assistant did after the user confirmed it (Fase 6c), with the
  -- data to undo it. Per device: the changes themselves sync like any other.
  CREATE TABLE ai_activity (
    id         TEXT PRIMARY KEY,
    created_at INTEGER NOT NULL,
    action     TEXT NOT NULL,
    summary    TEXT NOT NULL,
    page_id    TEXT,
    undo       TEXT NOT NULL,
    undone_at  INTEGER
  );
  CREATE INDEX ai_activity_created ON ai_activity(created_at);
  `,
  /* 8 */ `
  -- Workspaces (shared document spaces, Deel B): a page's workspace is set
  -- once at creation (see applyRemotePage), not per-field HLC merged. Workspace
  -- metadata/membership is pulled separately from the records CRDT path (the
  -- server is always authoritative; see engine.ts's pullWorkspaces).
  ALTER TABLE pages ADD COLUMN workspace_id TEXT;
  CREATE INDEX pages_workspace ON pages(workspace_id) WHERE workspace_id IS NOT NULL;

  CREATE TABLE workspaces (
    id         TEXT PRIMARY KEY,
    name       TEXT NOT NULL,
    created_by TEXT NOT NULL,
    created_at INTEGER NOT NULL
  );

  CREATE TABLE workspace_members (
    workspace_id TEXT NOT NULL REFERENCES workspaces(id),
    user_id      TEXT NOT NULL,
    permission   TEXT NOT NULL,
    PRIMARY KEY (workspace_id, user_id)
  );
  `,
  /* 9 */ `
  -- Workspace invites (pending until accepted) and per-page last-editor
  -- attribution (set server-side; see the 20261018150000 migration).
  ALTER TABLE pages ADD COLUMN last_edited_by TEXT;
  ALTER TABLE workspace_members ADD COLUMN status TEXT NOT NULL DEFAULT 'active';
  `,
  /* 10 */ `
  -- Search also finds the tags property. FTS5 cannot add a column, so rebuild.
  CREATE VIRTUAL TABLE page_fts_new USING fts5(
    page_id UNINDEXED, title, body, tags,
    tokenize = 'unicode61 remove_diacritics 2'
  );
  INSERT INTO page_fts_new(page_id, title, body, tags)
    SELECT f.page_id, f.title, f.body, COALESCE((
      SELECT group_concat(t.value, ' ') FROM page_props pp, json_each(pp.value) t
      WHERE pp.page_id = f.page_id AND pp.key = 'tags' AND pp.deleted = 0
        AND json_valid(pp.value)), '')
    FROM page_fts f;
  DROP TABLE page_fts;
  ALTER TABLE page_fts_new RENAME TO page_fts;
  `,
  /* 11 */ `
  -- Backups restored before the needs_resync marker existed (v0.3.1) never
  -- reached the server: their pages only went out once edited again. Push
  -- everything once on every device; per-field clocks and Yjs make
  -- re-pushing what the server already has harmless.
  INSERT OR REPLACE INTO kv(key, value) VALUES ('needs_resync', '1');
  `,
  /* 12 */ `
  -- Those same restores also copied the other device's device_id, so both
  -- skipped each other's content. Every device takes a new id once and pulls
  -- all content again (see Store.open).
  INSERT OR REPLACE INTO kv(key, value) VALUES ('reset_device_id', '1');
  `,
];

export async function migrate(db: SqlDriver): Promise<number> {
  const [row] = await db.query<{ user_version: number }>('PRAGMA user_version');
  const current = row?.user_version ?? 0;
  if (current > migrations.length) {
    throw new Error(`Database schema v${current} is newer than this app (v${migrations.length})`);
  }
  for (let v = current; v < migrations.length; v++) {
    await db.batch([{ sql: migrations[v]! }, { sql: `PRAGMA user_version = ${v + 1}` }]);
  }
  return migrations.length;
}
